import api from './api';

export type UnavailableReason = 'booked' | 'owner_unavailable';

export interface UnavailableRange {
  startDate: string;
  endDate: string;
  reason: UnavailableReason;
}

export interface OwnerUnavailablePeriod {
  id: number;
  startDate: string;
  endDate: string;
}

export interface ProductAvailability {
  unavailableRanges: UnavailableRange[];
}

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function parseYmdToLocalDate(value: string): Date | undefined {
  const match = ISO_DATE_RE.exec(value.split('T')[0]);
  if (!match) return undefined;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

export function formatLocalDateToYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function todayYmd(): string {
  return formatLocalDateToYmd(new Date());
}

function ymdToUtcMs(value: string): number | null {
  const match = ISO_DATE_RE.exec(value.split('T')[0]);
  if (!match) return null;
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** Inclusive: a calendar day falls inside any unavailable range. */
export function isDateInUnavailableRanges(ymd: string, ranges: UnavailableRange[]): boolean {
  const day = ymdToUtcMs(ymd);
  if (day === null) return false;
  return ranges.some(r => {
    const start = ymdToUtcMs(r.startDate);
    const end = ymdToUtcMs(r.endDate);
    return start !== null && end !== null && day >= start && day <= end;
  });
}

/** Inclusive range overlap with any unavailable range (shared boundary day counts). */
export function rangeOverlapsUnavailable(
  startYmd: string,
  endYmd: string,
  ranges: UnavailableRange[],
): boolean {
  const start = ymdToUtcMs(startYmd);
  const end = ymdToUtcMs(endYmd);
  if (start === null || end === null || end < start) return false;
  return ranges.some(r => {
    const uStart = ymdToUtcMs(r.startDate);
    const uEnd = ymdToUtcMs(r.endDate);
    if (uStart === null || uEnd === null) return false;
    return start <= uEnd && uStart <= end;
  });
}

/** Latest rental end, counted from today. Mirrors RentalPeriod.MaxRentalEndDaysAhead on the backend. */
export const MAX_RENTAL_END_DAYS_AHEAD = 60;

export function addDaysYmd(fromYmd: string, days: number): string {
  const date = parseYmdToLocalDate(fromYmd);
  if (!date) return fromYmd;
  date.setDate(date.getDate() + days);
  return formatLocalDateToYmd(date);
}

export function addMonthsYmd(fromYmd: string, months: number): string {
  const date = parseYmdToLocalDate(fromYmd);
  if (!date) return fromYmd;
  date.setMonth(date.getMonth() + months);
  return formatLocalDateToYmd(date);
}

export async function fetchProductAvailability(
  productId: number,
  from?: string,
  to?: string,
): Promise<UnavailableRange[]> {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const qs = params.toString();
  const res = await api.get<ProductAvailability>(
    `/products/${productId}/availability${qs ? `?${qs}` : ''}`,
  );
  return res.data.unavailableRanges ?? [];
}

export async function fetchOwnerUnavailablePeriods(): Promise<OwnerUnavailablePeriod[]> {
  const res = await api.get<OwnerUnavailablePeriod[]>('/users/unavailable-periods');
  return res.data;
}

export async function createOwnerUnavailablePeriod(
  startDate: string,
  endDate: string,
): Promise<OwnerUnavailablePeriod> {
  const res = await api.post<OwnerUnavailablePeriod>('/users/unavailable-periods', {
    startDate: new Date(startDate).toISOString(),
    endDate: new Date(endDate).toISOString(),
  });
  return res.data;
}

export async function deleteOwnerUnavailablePeriod(id: number): Promise<void> {
  await api.delete(`/users/unavailable-periods/${id}`);
}